import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiX,
} from "react-icons/fi";

import api from "../services/api";
import { invoiceApi } from "../services/invoiceApi";
import { settingsApi } from "../services/settingsApi";

const money = (value) => Number(Number(value || 0).toFixed(2));

const currencySymbol = (currency = "GBP") => {
  const code = String(currency || "GBP").toUpperCase();

  const symbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    AED: "د.إ",
    SAR: "﷼",
  };

  return symbols[code] || code;
};

const formatMoney = (value, currency = "GBP") => {
  return `${currencySymbol(currency)}${Number(value || 0).toFixed(2)}`;
};

const emptyItem = () => ({
  costPriceId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  taxRate: "",
});

const paymentModes = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];


const currencyOptions = ["GBP", "USD", "EUR", "PKR", "AED", "SAR"];

const getFreightLabel = (item) => {
  return (
    item?.routeName ||
    item?.productName ||
    item?.description ||
    item?.carrier ||
    item?.forwarder ||
    `Freight Rate ${item?.id || ""}`
  );
};

const getFreightAmount = (item) => {
  return Number(
    item?.totalCost ??
      item?.total ??
      item?.oceanFreight ??
      item?.cost ??
      item?.unitPrice ??
      item?.price ??
      0
  );
};

function Toast({ open, type = "success", title, message, onClose }) {
  if (!open) return null;

  const isError = type === "error";

  return (
    <div className="fixed right-5 top-5 z-[10000] w-[calc(100%-40px)] max-w-md">
      <div
        className={`flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-2xl dark:bg-slate-900 ${
          isError
            ? "border-red-100 text-red-700 dark:border-red-900 dark:text-red-300"
            : "border-emerald-100 text-emerald-700 dark:border-emerald-900 dark:text-emerald-300"
        }`}
      >
        <div
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${
            isError
              ? "bg-red-50 dark:bg-red-950"
              : "bg-emerald-50 dark:bg-emerald-950"
          }`}
        >
          {isError ? (
            <FiAlertCircle className="text-lg" />
          ) : (
            <FiCheckCircle className="text-lg" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            {title || (isError ? "Error" : "Success")}
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          aria-label="Close message"
        >
          <FiX />
        </button>
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function MoneyBlock({ label, value, currency }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
        {formatMoney(value, currency)}
      </p>
    </div>
  );
}

export default function InvoiceCreate() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [user, setUser] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [costPrices, setCostPrices] = useState([]);

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const [settings, setSettings] = useState({
    companyName: "",
    companyAddress: "",
    companyEmail: "",
    companyPhone: "",
    logoDataUrl: "",
    invoiceShowLogo: true,
    invoiceShowTitle: true,
    invoiceTitle: "INVOICE",
    invoiceCurrency: "GBP",
    invoiceTaxEnabled: false,
    invoiceTaxRate: 0,
    invoiceDiscountEnabled: false,
    invoiceDiscountRate: 0,
  });

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("bank_transfer");
  const [statusMode, setStatusMode] = useState("auto");
  const [currency, setCurrency] = useState("GBP");
  const [taxRate, setTaxRate] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  const showToast = (
    type,
    message,
    title = type === "success" ? "Success" : "Error"
  ) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });

    window.setTimeout(() => {
      setToast((prev) => ({ ...prev, open: false }));
    }, 3000);
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const [meRes, settingsRes, vendorsRes, costPricesRes, numberRes] =
          await Promise.allSettled([
            api.get("/auth/me"),
            settingsApi.getMy(),
            api.get("/vendors"),
            api.get("/cost-prices"),
            invoiceApi.getNextNumber(),
          ]);

        if (meRes.status === "fulfilled") {
          setUser(meRes.value.data?.user || null);
        }

        if (settingsRes.status === "fulfilled") {
          const s = settingsRes.value.data?.settings || {};

          const nextSettings = {
            companyName: s.companyName || "",
            companyAddress: s.companyAddress || "",
            companyEmail: s.companyEmail || "",
            companyPhone: s.companyPhone || "",
            logoDataUrl: s.logoDataUrl || "",
            invoiceShowLogo: s.invoiceShowLogo !== false,
            invoiceShowTitle: s.invoiceShowTitle !== false,
            invoiceTitle: s.invoiceTitle || "INVOICE",
            invoiceCurrency: s.invoiceCurrency || "GBP",
            invoiceTaxEnabled: Boolean(s.invoiceTaxEnabled),
            invoiceTaxRate: Number(s.invoiceTaxRate || 0),
            invoiceDiscountEnabled: Boolean(s.invoiceDiscountEnabled),
            invoiceDiscountRate: Number(s.invoiceDiscountRate || 0),
          };

          setSettings(nextSettings);
          setCurrency(nextSettings.invoiceCurrency);
          setTaxRate(nextSettings.invoiceTaxRate);
          setDiscountRate(nextSettings.invoiceDiscountRate);
        }

        if (vendorsRes.status === "fulfilled") {
          const data = vendorsRes.value.data;
          setVendors(Array.isArray(data?.vendors) ? data.vendors : data || []);
        }

        if (costPricesRes.status === "fulfilled") {
          const data = costPricesRes.value.data;
          setCostPrices(
            Array.isArray(data?.costPrices)
              ? data.costPrices
              : Array.isArray(data)
              ? data
              : []
          );
        }

        if (numberRes.status === "fulfilled") {
          setInvoiceNumber(numberRes.value.data?.invoiceNumber || "");
        }
      } catch {
        showToast("error", "Failed to load invoice page.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const activeTaxRate = settings.invoiceTaxEnabled
    ? Number(settings.invoiceTaxRate || 0)
    : Number(taxRate || 0);

  const activeDiscountRate = settings.invoiceDiscountEnabled
    ? Number(settings.invoiceDiscountRate || 0)
    : Number(discountRate || 0);

  const totals = useMemo(() => {
    let subtotal = 0;
    let taxAmount = 0;

    items.forEach((item) => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const lineBase = quantity * unitPrice;

      const lineTaxRate =
        item.taxRate === "" ||
        item.taxRate === null ||
        item.taxRate === undefined
          ? activeTaxRate
          : Number(item.taxRate || 0);

      subtotal += lineBase;
      taxAmount += lineBase * (lineTaxRate / 100);
    });

    subtotal = money(subtotal);
    taxAmount = money(taxAmount);

    const discountAmount = money(subtotal * (activeDiscountRate / 100));
    const totalAmount = money(subtotal + taxAmount - discountAmount);
    const balanceAmount = totalAmount;

    return {
      subtotal,
      taxAmount,
      discountAmount,
      totalAmount,
      balanceAmount,
    };
  }, [items, activeTaxRate, activeDiscountRate]);

  const setItemField = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updated = { ...item, [field]: value };

        if (field === "costPriceId") {
          const selected = costPrices.find(
            (cp) => String(cp.id) === String(value)
          );

          if (selected) {
            updated.description = getFreightLabel(selected);
            updated.unitPrice = getFreightAmount(selected);

            if (!updated.taxRate && activeTaxRate) {
              updated.taxRate = activeTaxRate;
            }
          }
        }

        return updated;
      })
    );
  };

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItem = (index) => {
    setItems((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (!invoiceDate) {
        throw new Error("Invoice date is required");
      }

      if (!vendorId && !supplierName.trim()) {
        throw new Error("Please select a forwarder or enter supplier name");
      }

      const cleanedItems = items
        .map((item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          const lineBase = money(quantity * unitPrice);

          const resolvedTaxRate =
            item.taxRate === "" ||
            item.taxRate === null ||
            item.taxRate === undefined
              ? activeTaxRate
              : Number(item.taxRate || 0);

          return {
            costPriceId: item.costPriceId ? Number(item.costPriceId) : null,
            description: (item.description || "").trim(),
            quantity,
            unitPrice,
            taxRate: resolvedTaxRate,
            taxAmount: money(lineBase * (resolvedTaxRate / 100)),
          };
        })
        .filter((item) => item.description && item.quantity > 0);

      if (!cleanedItems.length) {
        throw new Error("Add at least one invoice item");
      }

      const selectedVendor = vendors.find(
        (vendor) => String(vendor.id) === String(vendorId)
      );

      const payload = {
        invoiceNumber: invoiceNumber.trim() || undefined,
        vendorId: vendorId ? Number(vendorId) : null,
        supplierName: supplierName.trim() || selectedVendor?.name || null,
        invoiceDate,
        dueDate: dueDate || null,
        paymentMode,
        status: statusMode === "draft" ? "draft" : undefined,
        currency,
        taxMode: "percent",
        taxRate: activeTaxRate,
        taxAmount: totals.taxAmount,
        discountAmount: totals.discountAmount,
        notes: notes.trim() || null,
        items: cleanedItems,
      };

      const res = await invoiceApi.create(payload);
      const createdId = res.data?.invoice?.id;

      showToast("success", "Invoice created successfully");

      window.setTimeout(() => {
        navigate(createdId ? `/invoices/${createdId}/print` : "/invoices");
      }, 700);
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message || err.message || "Failed to create invoice"
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  const labelClass =
    "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300";

  const tableInputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-950">
        <Card className="p-6 dark:text-slate-100">
          Loading invoice page...
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 md:p-6">
      <Toast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />

      <form onSubmit={handleCreate}>
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <Card className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  {settings.invoiceShowTitle ? (
                    <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {settings.invoiceTitle || "INVOICE"}
                    </h1>
                  ) : null}

                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    Create freight invoice for forwarder, carrier, customs,
                    port or delivery costs.
                  </p>
                </div>

                {settings.invoiceShowLogo && settings.logoDataUrl ? (
                  <img
                    src={settings.logoDataUrl}
                    alt="Logo"
                    className="h-20 w-20 rounded-2xl object-contain"
                  />
                ) : null}
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={labelClass}>Invoice #</label>
                  <input
                    value={invoiceNumber}
                    readOnly
                    className={`${inputClass} cursor-not-allowed bg-slate-100 dark:bg-slate-800`}
                  />
                </div>

                <div>
                  <label className={labelClass}>Forwarder</label>
                  <select
                    value={vendorId}
                    onChange={(e) => {
                      setVendorId(e.target.value);

                      const selected = vendors.find(
                        (vendor) => String(vendor.id) === String(e.target.value)
                      );

                      if (selected) {
                        setSupplierName(selected.name || "");
                      }
                    }}
                    className={inputClass}
                  >
                    <option value="">Select forwarder</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name || `Forwarder ${vendor.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Supplier / Forwarder Name
                  </label>
                  <input
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    className={inputClass}
                    placeholder="Forwarder name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Payment Mode</label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className={inputClass}
                  >
                    {paymentModes.map((mode) => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={statusMode}
                    onChange={(e) => setStatusMode(e.target.value)}
                    className={inputClass}
                  >
                    <option value="auto">
                      Automatic — based on due date and payments
                    </option>
                    <option value="draft">Draft</option>
                  </select>
                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Pending, partial, paid, and overdue are calculated automatically.
                  </p>
                </div>

                <div>
                  <label className={labelClass}>Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className={inputClass}
                  >
                    {currencyOptions.map((cur) => (
                      <option key={cur} value={cur}>
                        {cur}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Invoice Items
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Use freight rates or add manual logistics charges.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addItem}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  + Add Item
                </button>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-3">Freight Rate</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Qty</th>
                      <th className="px-4 py-3">Unit Price</th>
                      <th className="px-4 py-3">Tax %</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {items.map((item, index) => {
                      const lineAmount = money(
                        Number(item.quantity || 0) * Number(item.unitPrice || 0)
                      );

                      return (
                        <tr
                          key={index}
                          className="bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        >
                          <td className="px-4 py-3">
                            <select
                              value={item.costPriceId}
                              onChange={(e) =>
                                setItemField(
                                  index,
                                  "costPriceId",
                                  e.target.value
                                )
                              }
                              className={tableInputClass}
                            >
                              <option value="">Manual item</option>
                              {costPrices.map((cp) => (
                                <option key={cp.id} value={cp.id}>
                                  {getFreightLabel(cp)} —{" "}
                                  {formatMoney(
                                    getFreightAmount(cp),
                                    cp.currency || currency
                                  )}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="px-4 py-3">
                            <input
                              value={item.description}
                              onChange={(e) =>
                                setItemField(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              className={tableInputClass}
                              placeholder="Freight, customs, port charges..."
                            />
                          </td>

                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity}
                              onChange={(e) =>
                                setItemField(index, "quantity", e.target.value)
                              }
                              className={tableInputClass}
                            />
                          </td>

                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unitPrice}
                              onChange={(e) =>
                                setItemField(index, "unitPrice", e.target.value)
                              }
                              className={tableInputClass}
                            />
                          </td>

                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.taxRate}
                              onChange={(e) =>
                                setItemField(index, "taxRate", e.target.value)
                              }
                              className={tableInputClass}
                              placeholder={String(activeTaxRate)}
                            />
                          </td>

                          <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                            {formatMoney(lineAmount, currency)}
                          </td>

                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                              className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="p-5">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Invoice Settings
              </h3>

              <div className="mt-4 space-y-4">
                <div>
                  <label className={labelClass}>Tax %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={activeTaxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className={inputClass}
                    disabled={settings.invoiceTaxEnabled}
                  />
                </div>

                <div>
                  <label className={labelClass}>Discount %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={activeDiscountRate}
                    onChange={(e) => setDiscountRate(e.target.value)}
                    className={inputClass}
                    disabled={settings.invoiceDiscountEnabled}
                  />
                </div>

                <div>
                  <label className={labelClass}>Paid Amount</label>
                  <input
                    type="text"
                    value={formatMoney(0, currency)}
                    className={inputClass}
                    readOnly
                    disabled
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Create the invoice first, then record payment from the Payments module.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Totals
              </h3>

              <div className="mt-4 space-y-3">
                <MoneyBlock
                  label="Subtotal"
                  value={totals.subtotal}
                  currency={currency}
                />
                <MoneyBlock
                  label="Tax"
                  value={totals.taxAmount}
                  currency={currency}
                />
                <MoneyBlock
                  label="Discount"
                  value={totals.discountAmount}
                  currency={currency}
                />
                <MoneyBlock
                  label="Total"
                  value={totals.totalAmount}
                  currency={currency}
                />
                <MoneyBlock
                  label="Balance"
                  value={totals.balanceAmount}
                  currency={currency}
                />
              </div>
            </Card>

            <Card className="p-5">
              <label className={labelClass}>Notes</label>
              <textarea
                rows="4"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputClass}
                placeholder="Optional notes"
              />

              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/invoices")}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full rounded-2xl bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-70"
                >
                  {saving ? "Saving..." : "Create Invoice"}
                </button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}